/**
 * Amazing Nature – Core Theme JavaScript
 * Handles: navigation, cart drawer, sticky ATC, animations,
 *          quantity selectors, announcement bar dismiss,
 *          product gallery, mobile menu, search, accordions.
 */

'use strict';

/* ─────────────────────────────────────────────────────────────
   UTILITIES
───────────────────────────────────────────────────────────── */
const $ = (sel, ctx = document) => ctx.querySelector(sel);
const $$ = (sel, ctx = document) => [...ctx.querySelectorAll(sel)];
const on = (el, evt, fn, opts) => el?.addEventListener(evt, fn, opts);
const off = (el, evt, fn) => el?.removeEventListener(evt, fn);

function debounce(fn, wait = 200) {
  let timer;
  return (...args) => { clearTimeout(timer); timer = setTimeout(() => fn(...args), wait); };
}

function trapFocus(el) {
  const focusable = $$('a, button, input, textarea, select, [tabindex]:not([tabindex="-1"])', el)
    .filter(e => !e.disabled && e.offsetParent !== null);
  if (!focusable.length) return;
  const first = focusable[0];
  const last  = focusable[focusable.length - 1];
  on(el, 'keydown', e => {
    if (e.key !== 'Tab') return;
    if (e.shiftKey) {
      if (document.activeElement === first) { e.preventDefault(); last.focus(); }
    } else {
      if (document.activeElement === last)  { e.preventDefault(); first.focus(); }
    }
  });
  first.focus();
}

/* ─────────────────────────────────────────────────────────────
   SCROLL ANIMATIONS (IntersectionObserver)
───────────────────────────────────────────────────────────── */
function initAnimations() {
  if (window.matchMedia('(prefers-reduced-motion: reduce)').matches) {
    $$('.an-fade-up').forEach(el => el.classList.add('is-visible'));
    return;
  }

  const observer = new IntersectionObserver(
    entries => entries.forEach(entry => {
      if (entry.isIntersecting) {
        entry.target.classList.add('is-visible');
        observer.unobserve(entry.target);
      }
    }),
    { threshold: 0.08, rootMargin: '0px 0px -32px 0px' }
  );

  $$('.an-fade-up').forEach(el => observer.observe(el));
}

/* ─────────────────────────────────────────────────────────────
   HEADER – sticky shadow + scroll direction detection
───────────────────────────────────────────────────────────── */
function initHeader() {
  const header = $('#SiteHeader');
  if (!header) return;

  let lastY = window.scrollY;

  const update = debounce(() => {
    const y = window.scrollY;
    header.classList.toggle('is-scrolled', y > 16);
    lastY = y;
  }, 10);

  on(window, 'scroll', update, { passive: true });
}

/* ─────────────────────────────────────────────────────────────
   ANNOUNCEMENT BAR – dismiss with localStorage persistence
───────────────────────────────────────────────────────────── */
function initAnnouncementBar() {
  const bar = $('[data-announcement-bar]');
  const btn = $('[data-dismiss-announcement]');
  if (!bar || !btn) return;

  if (localStorage.getItem('an_announcement_dismissed') === '1') {
    bar.remove();
    return;
  }

  on(btn, 'click', () => {
    bar.style.maxHeight = bar.offsetHeight + 'px';
    requestAnimationFrame(() => {
      bar.style.transition = 'max-height 300ms ease, opacity 300ms ease';
      bar.style.maxHeight  = '0';
      bar.style.opacity    = '0';
      bar.style.overflow   = 'hidden';
    });
    setTimeout(() => bar.remove(), 320);
    localStorage.setItem('an_announcement_dismissed', '1');
  });
}

/* ─────────────────────────────────────────────────────────────
   NAVIGATION – desktop dropdowns + mobile menu
───────────────────────────────────────────────────────────── */
function initNavigation() {
  /* Desktop dropdowns */
  $$('.nav-item--has-dropdown').forEach(item => {
    const trigger   = $('[data-nav-trigger]', item);
    const dropdown  = item.querySelector('.nav-dropdown');
    if (!trigger || !dropdown) return;

    let closeTimer;

    function open() {
      clearTimeout(closeTimer);
      item.classList.add('is-open');
      trigger.setAttribute('aria-expanded', 'true');
      dropdown.removeAttribute('hidden');
    }

    function close() {
      closeTimer = setTimeout(() => {
        item.classList.remove('is-open');
        trigger.setAttribute('aria-expanded', 'false');
        dropdown.setAttribute('hidden', '');
      }, 100);
    }

    on(item, 'mouseenter', open);
    on(item, 'mouseleave', close);
    on(trigger, 'click', () => item.classList.contains('is-open') ? close() : open());
    on(trigger, 'keydown', e => {
      if (e.key === 'Escape') { close(); trigger.focus(); }
    });
    on(dropdown, 'mouseenter', () => clearTimeout(closeTimer));
    on(dropdown, 'mouseleave', close);
  });

  /* Mobile hamburger + drawer */
  const hamburger = $('[data-hamburger]');
  const mobileNav = $('#MobileNav');
  const navClose  = $('[data-nav-close]');
  const overlay   = $('#overlay');

  function openMobileNav() {
    mobileNav?.removeAttribute('hidden');
    setTimeout(() => mobileNav?.classList.add('is-open'), 10);
    overlay?.classList.add('is-active');
    hamburger?.setAttribute('aria-expanded', 'true');
    if (mobileNav) trapFocus(mobileNav);
    document.body.style.overflow = 'hidden';
  }

  function closeMobileNav() {
    mobileNav?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
    hamburger?.setAttribute('aria-expanded', 'false');
    document.body.style.overflow = '';
    setTimeout(() => mobileNav?.setAttribute('hidden', ''), 400);
  }

  on(hamburger, 'click', openMobileNav);
  on(navClose,  'click', closeMobileNav);
  on(overlay,   'click', closeMobileNav);
  on(document,  'keydown', e => { if (e.key === 'Escape') closeMobileNav(); });

  /* Mobile sub-menus */
  $$('[data-mobile-trigger]').forEach(btn => {
    const item    = btn.closest('.mobile-nav__item--has-children');
    const submenu = item?.querySelector('.mobile-nav__submenu');
    if (!submenu) return;

    on(btn, 'click', () => {
      const expanded = btn.getAttribute('aria-expanded') === 'true';
      btn.setAttribute('aria-expanded', String(!expanded));
      item.classList.toggle('is-expanded', !expanded);
      if (!expanded) {
        submenu.removeAttribute('hidden');
      } else {
        submenu.setAttribute('hidden', '');
      }
    });
  });

  /* Search */
  const searchOpen  = $('[data-search-open]');
  const searchClose = $('[data-search-close]');
  const searchBar   = $('#HeaderSearch');
  const searchInput = $('[data-search-input]');

  on(searchOpen, 'click', () => {
    searchBar?.removeAttribute('hidden');
    searchInput?.focus();
  });
  on(searchClose, 'click', () => {
    searchBar?.setAttribute('hidden', '');
  });
  on(document, 'keydown', e => {
    if (e.key === 'Escape' && searchBar && !searchBar.hidden) {
      searchBar.setAttribute('hidden', '');
    }
  });
}

/* ─────────────────────────────────────────────────────────────
   CART DRAWER
───────────────────────────────────────────────────────────── */
function initCart() {
  const cartOpen    = $$('[data-cart-open]');
  const cartDrawer  = $('#CartDrawer');
  const cartClose   = $('[data-cart-close]');
  const overlay     = $('#overlay');

  function openCart() {
    cartDrawer?.classList.add('is-open');
    overlay?.classList.add('is-active');
    document.body.style.overflow = 'hidden';
    if (cartDrawer) trapFocus(cartDrawer);
  }

  function closeCart() {
    cartDrawer?.classList.remove('is-open');
    overlay?.classList.remove('is-active');
    document.body.style.overflow = '';
  }

  cartOpen.forEach(btn => on(btn, 'click', openCart));
  on(cartClose,  'click', closeCart);
  on(overlay,    'click', closeCart);
  document.addEventListener('cart:open', openCart);

  /* Update cart count badge */
  document.addEventListener('cart:updated', e => {
    const count = e.detail?.item_count ?? 0;
    const badge = $('#CartCount');
    if (!badge) return;
    badge.textContent = count;
    badge.classList.toggle('header__cart-count--hidden', count === 0);
    if (count > 0) {
      badge.animate([
        { transform: 'scale(1.4)' },
        { transform: 'scale(1)' }
      ], { duration: 300, easing: 'cubic-bezier(0.34,1.56,0.64,1)' });
    }
  });
}

/* ─────────────────────────────────────────────────────────────
   PRODUCT FORM – add to cart
───────────────────────────────────────────────────────────── */
function initProductForm() {
  const form   = $('#ProductForm');
  const loader = $('#AtcLoader');
  if (!form) return;

  on(form, 'submit', async e => {
    e.preventDefault();
    loader?.classList.add('is-loading');

    try {
      const formData = new FormData(form);
      const resp = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body: formData
      });

      if (!resp.ok) throw new Error('Cart add failed');
      const data = await resp.json();

      /* Fetch updated cart count */
      const cartResp  = await fetch('/cart.js');
      const cartData  = await cartResp.json();

      document.dispatchEvent(new CustomEvent('cart:updated', { detail: cartData }));
      document.dispatchEvent(new CustomEvent('cart:item-added', { detail: data }));

      /* Open cart drawer */
      $('#CartDrawer')?.classList.add('is-open');
      $('#overlay')?.classList.add('is-active');
      document.body.style.overflow = 'hidden';

      showToast('Produkt hinzugefügt ✓');

    } catch (err) {
      showToast('Fehler beim Hinzufügen. Bitte erneut versuchen.');
      console.error(err);
    } finally {
      loader?.classList.remove('is-loading');
    }
  });
}

/* ─────────────────────────────────────────────────────────────
   QUANTITY SELECTORS
───────────────────────────────────────────────────────────── */
function initQuantitySelectors() {
  on(document, 'click', e => {
    const minus = e.target.closest('[data-quantity-minus]');
    const plus  = e.target.closest('[data-quantity-plus]');
    if (!minus && !plus) return;

    const wrap  = (minus || plus).closest('.quantity');
    const input = $('input', wrap);
    if (!input) return;

    const val = parseInt(input.value, 10) || 1;
    input.value = minus ? Math.max(1, val - 1) : Math.min(99, val + 1);
    input.dispatchEvent(new Event('change', { bubbles: true }));
  });
}

/* ─────────────────────────────────────────────────────────────
   STICKY ATC (mobile product page)
───────────────────────────────────────────────────────────── */
function initStickyAtc() {
  const atcBar    = $('#StickyAtc');
  const buyBox    = $('#BuyBox');
  const stickyBtn = $('[data-sticky-atc-btn]');
  if (!atcBar || !buyBox) return;

  const observer = new IntersectionObserver(
    ([entry]) => {
      const visible = !entry.isIntersecting;
      atcBar.removeAttribute('hidden');
      atcBar.classList.toggle('is-visible', visible);
    },
    { threshold: 0, rootMargin: '-80px 0px 0px 0px' }
  );

  observer.observe(buyBox);

  /* Sticky button clicks submit the main product form */
  on(stickyBtn, 'click', () => {
    $('#AddToCart')?.click();
  });
}

/* ─────────────────────────────────────────────────────────────
   PRODUCT GALLERY
───────────────────────────────────────────────────────────── */
function initProductGallery() {
  const gallery = $('#ProductGallery');
  if (!gallery) return;

  const slides = $$('.product-gallery__slide', gallery);
  const thumbs = $$('.product-gallery__thumb', gallery);

  function goTo(index) {
    slides.forEach((s, i) => {
      s.classList.toggle('is-active', i === index);
    });
    thumbs.forEach((t, i) => {
      t.classList.toggle('is-active', i === index);
      t.setAttribute('aria-pressed', String(i === index));
    });
  }

  thumbs.forEach((thumb, i) => {
    on(thumb, 'click', () => goTo(i));
  });

  /* Swipe support on mobile */
  let touchStartX = 0;
  let currentIndex = 0;

  on(gallery, 'touchstart', e => {
    touchStartX = e.changedTouches[0].clientX;
  }, { passive: true });

  on(gallery, 'touchend', e => {
    const dx = e.changedTouches[0].clientX - touchStartX;
    if (Math.abs(dx) < 40) return;
    const next = dx < 0
      ? Math.min(slides.length - 1, currentIndex + 1)
      : Math.max(0, currentIndex - 1);
    if (next !== currentIndex) {
      currentIndex = next;
      goTo(next);
    }
  }, { passive: true });
}

/* ─────────────────────────────────────────────────────────────
   SUBSCRIPTION WIDGET
───────────────────────────────────────────────────────────── */
function initSubscriptionWidget() {
  const widget  = $('.product-hero__subscription');
  if (!widget) return;

  const radios  = $$('[data-purchase-type]', widget);

  radios.forEach(radio => {
    on(radio, 'change', () => {
      /* Hook for subscription app integration (e.g. ReCharge / Shopify Subscriptions) */
      document.dispatchEvent(new CustomEvent('subscription:changed', {
        detail: { type: radio.value }
      }));
    });
  });
}

/* ─────────────────────────────────────────────────────────────
   TOAST NOTIFICATION
───────────────────────────────────────────────────────────── */
let toastTimer;
function showToast(message, duration = 2800) {
  const toast = $('#toast');
  if (!toast) return;
  clearTimeout(toastTimer);
  toast.textContent = message;
  toast.classList.add('is-visible');
  toastTimer = setTimeout(() => toast.classList.remove('is-visible'), duration);
}

/* ─────────────────────────────────────────────────────────────
   ACCORDION (details/summary)
───────────────────────────────────────────────────────────── */
function initAccordions() {
  /* Enhance native <details> with ARIA and animation */
  $$('.accordion__item').forEach(details => {
    const summary = $('summary', details);
    if (!summary) return;

    /* Sync aria-expanded */
    const syncAria = () => {
      summary.setAttribute('aria-expanded', String(details.open));
    };

    on(details, 'toggle', syncAria);
    syncAria();
  });
}

/* ─────────────────────────────────────────────────────────────
   NOTIFY ME (sold-out products)
───────────────────────────────────────────────────────────── */
function initNotifyMe() {
  on(document, 'click', e => {
    const btn = e.target.closest('[data-notify-product]');
    if (!btn) return;
    showToast('Benachrichtigungsanfrage gesendet.');
    /* Integrate with your email/back-in-stock app here */
  });
}

/* ─────────────────────────────────────────────────────────────
   INITIALISE
───────────────────────────────────────────────────────────── */
document.addEventListener('DOMContentLoaded', () => {
  initAnimations();
  initHeader();
  initAnnouncementBar();
  initNavigation();
  initCart();
  initProductForm();
  initQuantitySelectors();
  initStickyAtc();
  initProductGallery();
  initSubscriptionWidget();
  initAccordions();
  initNotifyMe();
});
